import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

import { PlatformEventsService } from '../../core/services/platform-events.service';
import { SnackbarService } from '../../core/services/snackbar.service';
import { UserService } from '../../core/services/user.service';
import { UNSKIPPABLE_AD_VIDEOS } from '../../core/utils/unskippable-ad.utils';
import { HubspotEventNameEnum } from '../../models/hubspot-tracking-event';
import { VimeoPlayerComponent } from '../../ui-kit/vimeo-player/vimeo-player.component';

@Component({
  selector: 'app-unskippable-banner',
  imports: [MatButtonModule, MatIconModule, VimeoPlayerComponent],
  templateUrl: './unskippable-banner.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./unskippable-banner.component.scss'],
})
export class UnskippableBannerComponent implements OnInit, OnDestroy {
  private readonly videos = UNSKIPPABLE_AD_VIDEOS;

  currentVideoId!: number;
  private currentVideoPageKey!: string;

  skipCountdown = 5;
  hasSentEmail = false;
  private countdownInterval?: ReturnType<typeof setInterval>;
  isPlayerLoading = true;

  private userService = inject(UserService);
  private router = inject(Router);
  private location = inject(Location);
  private snackbarService = inject(SnackbarService);
  private platformEventsService = inject(PlatformEventsService);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const nextVideo = this.getNextUnwatchedVideo();

    if (!nextVideo) {
      this.navigateToPreviousPage();
      return;
    }

    this.currentVideoPageKey = nextVideo.pageKey;
    this.currentVideoId = nextVideo.videoId;
    this.startSkipCountdown();
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  onVideoLoaded(): void {
    this.isPlayerLoading = false;
  }

  onVideoEnded(): void {
    this.markCurrentVideoAsWatched();
    this.navigateToPreviousPage();
  }

  private startSkipCountdown(): void {
    this.countdownInterval = setInterval(() => {
      this.skipCountdown -= 1;
      if (this.skipCountdown <= 0) {
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  private getNextUnwatchedVideo(): { pageKey: string; videoId: number } | null {
    const user = this.userService.getUser();
    const visitedPages = user.visited_pages || [];

    const unwatchedVideo = Object.entries(this.videos).find(([pageKey]) => !visitedPages.includes(pageKey));

    if (unwatchedVideo) {
      const [pageKey, videoId] = unwatchedVideo;
      return {
        pageKey,
        videoId,
      };
    }

    return null;
  }

  private markCurrentVideoAsWatched(): void {
    this.userService
      .checkAndMarkPageAsVisited(this.currentVideoPageKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  connectNylas(): void {
    this.hasSentEmail = true;
  }

  navigateToPreviousPage(): void {
    this.markCurrentVideoAsWatched();
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(['/']);
    }
  }

  showLearnMoreSnackbar(): void {
    this.hasSentEmail = true;
    this.platformEventsService.trackHubspotCustomEvent(HubspotEventNameEnum.LEARN_MORE_EARLY_PAY);
    this.snackbarService.info('An email has been sent with more information');
  }
}
